
import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { translations, Language } from '@/locales';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => any;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      // Check if we have a saved language preference
      const saved = localStorage.getItem('language');
      if (saved) {
        return saved as Language;
      }

      // Detect browser language
      const browserLang = navigator.language || (navigator as any).userLanguage;
      const supportedLanguages = ['en', 'pl'];

      // Check if browser language is supported (exact match or language code match)
      for (const lang of supportedLanguages) {
        if (browserLang.toLowerCase() === lang || browserLang.toLowerCase().startsWith(lang + '-')) {
          return lang as Language;
        }
      }

      // Default to English if no match
      return 'en';
    } catch (error) {
      console.warn('Error detecting language, defaulting to English:', error);
      return 'en';
    }
  });

  const setLanguage = (lang: Language) => {
    try {
      setLanguageState(lang);
      localStorage.setItem('language', lang);
    } catch (error) {
      console.warn('Error setting language in localStorage:', error);
      setLanguageState(lang);
    }
  };

  // Translation function with fallback support
  const t = (key: string): any => {
    try {
      const currentTranslations = translations[language];

      if (!currentTranslations) {
        console.warn(`No translations found for language "${language}", falling back to English`);
        const englishTranslations = translations.en;
        if (!englishTranslations) {
          console.error('English translations not found');
          return key;
        }
      }

      // Helper function to get nested value from object using dot notation
      const getNestedValue = (obj: any, path: string): any => {
        return path.split('.').reduce((current, segment) => {
          return current && current[segment] !== undefined ? current[segment] : undefined;
        }, obj);
      };

      // Try to get translation from current language
      const translation = getNestedValue(currentTranslations, key);

      if (translation !== undefined) {
        return translation;
      }

      // Fallback to English if key not found and current language is not English
      if (language !== 'en') {
        const englishTranslation = getNestedValue(translations.en, key);
        if (englishTranslation !== undefined) {
          console.warn(`Translation missing for key "${key}" in language "${language}", falling back to English`);
          return englishTranslation;
        }
      }

      // If no translation found, return the key itself
      console.warn(`Translation missing for key "${key}" in both current language and English`);
      return key;
    } catch (error) {
      console.error('Error in translation function:', error);
      return key;
    }
  };

  useEffect(() => {
    try {
      document.documentElement.lang = language;
    } catch (error) {
      console.warn('Error setting document language:', error);
    }
  }, [language]);

  const contextValue = { language, setLanguage, t };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Re-export types for convenience
export type { Language };
